/**
 * Procedural Cell Renderer
 * Implements layered rendering pipeline from PRD Section 3.2
 *
 * Layers:
 * 1. Cytoplasm with noise texture
 * 2. Organelles with procedural placement
 * 3. Nucleus (if eukaryotic)
 * 4. Cell membrane with animated boundary
 * 5. Glow/highlight effects
 */

import Phaser from 'phaser';
import { SimplexNoise } from '@procedural/noise';
import { OrganelleRenderer } from './OrganelleRenderer';
import { CELL_COLORS, hslToHex } from '@/utils/colors';
import { SeededRandom } from '@/utils/math';
import type { ProceduralCellConfig, Vector2 } from '@/types';

export interface RenderedCellResult {
  container: Phaser.GameObjects.Container;
  membrane: Phaser.GameObjects.Graphics;
  cytoplasm: Phaser.GameObjects.Graphics;
  nucleus: Phaser.GameObjects.Graphics | null;
  organelles: Phaser.GameObjects.Graphics[];
  effects: Phaser.GameObjects.Graphics;
  config: ProceduralCellConfig;
  seed: number;
}

export class CellRenderer {
  private scene: Phaser.Scene;
  private organelleRenderer: OrganelleRenderer;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.organelleRenderer = new OrganelleRenderer(scene);
  }

  /**
   * Render a complete cell with all layers
   */
  renderCell(config: ProceduralCellConfig, seed: number = Date.now()): RenderedCellResult {
    const container = this.scene.add.container(0, 0);
    const noise = new SimplexNoise(seed);
    const random = new SeededRandom(seed);

    // Calculate actual size within range
    const size = random.range(config.size.min, config.size.max);
    const scaledConfig = this.scaleConfig(config, size);

    // Layer 1: Cytoplasm
    const cytoplasm = this.renderCytoplasm(scaledConfig, noise);
    container.add(cytoplasm);

    // Layer 2: Organelles
    const organelles = this.renderOrganelles(scaledConfig, noise, random);
    organelles.forEach(o => container.add(o));

    // Layer 3: Nucleus (if eukaryotic)
    let nucleus: Phaser.GameObjects.Graphics | null = null;
    if (scaledConfig.nucleus) {
      nucleus = this.renderNucleus(scaledConfig, noise);
      container.add(nucleus);
    }

    // Layer 4: Membrane
    const membrane = this.renderMembrane(scaledConfig, noise);
    container.add(membrane);

    // Layer 5: Effects (glow)
    const effects = this.renderEffects(scaledConfig);
    container.add(effects);

    return {
      container,
      membrane,
      cytoplasm,
      nucleus,
      organelles,
      effects,
      config: scaledConfig,
      seed,
    };
  }

  /**
   * Scale config based on actual size
   */
  private scaleConfig(config: ProceduralCellConfig, size: number): ProceduralCellConfig {
    const scale = size / config.membrane.baseRadius;
    return {
      ...config,
      membrane: {
        ...config.membrane,
        baseRadius: size,
        thickness: config.membrane.thickness * Math.sqrt(scale),
      },
      nucleus: config.nucleus ? {
        ...config.nucleus,
        relativeSize: config.nucleus.relativeSize,
      } : null,
    };
  }

  /**
   * Render cytoplasm (inner cell area)
   */
  private renderCytoplasm(
    config: ProceduralCellConfig,
    noise: SimplexNoise
  ): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    const { baseRadius, irregularity } = config.membrane;
    const shape = config.shape || 'spherical';

    // Fill with cytoplasm color
    const cytoColor = hslToHex(config.cytoplasm.color);

    if (shape === 'spiral') {
      // Use custom spiral drawing for spirillum
      const spinePoints = this.generateSpiralShape(baseRadius * 0.95, irregularity, 48, noise);
      const params = this.getSpiralParams(baseRadius);
      this.drawSpiralShape(graphics, spinePoints, params.thickness * 0.9, cytoColor, 0.6, 0, 0, 0);
    } else if (shape === 'rod') {
      // Use custom rod drawing for bacillus - no Catmull-Rom smoothing
      this.drawRodShape(graphics, baseRadius * 0.95, cytoColor, 0.4, 0, 0, 0);
    } else {
      // Standard shape rendering
      const points = this.generateShapePoints(config, baseRadius * 0.95, irregularity, 32, noise);

      graphics.fillStyle(cytoColor, 0.4);
      graphics.beginPath();
      graphics.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        graphics.lineTo(points[i].x, points[i].y);
      }
      graphics.closePath();
      graphics.fillPath();
    }

    // Add subtle texture with noise
    this.addCytoplasmTexture(graphics, baseRadius, config.cytoplasm.density, noise, shape);

    return graphics;
  }

  /**
   * Add texture to cytoplasm using noise
   */
  private addCytoplasmTexture(
    graphics: Phaser.GameObjects.Graphics,
    radius: number,
    density: number,
    noise: SimplexNoise,
    shape: 'spherical' | 'rod' | 'spiral' = 'spherical'
  ): void {
    const dotCount = Math.floor(density * 50);
    graphics.fillStyle(0xFFFFFF, 0.03);

    for (let i = 0; i < dotCount; i++) {
      let x: number, y: number;

      if (shape === 'rod') {
        // Distribute dots in elongated capsule area
        const halfLength = radius * 1.5;
        const halfHeight = radius * 0.18;
        x = (noise.noise2D(i, 0) * 2 - 1) * halfLength * 0.9;
        y = (noise.noise2DNormalized(i, 1) - 0.5) * halfHeight * 1.8;
      } else if (shape === 'spiral') {
        // Distribute dots along the wavy spirillum shape
        const length = radius * 5;
        const amplitude = radius * 0.5;
        const waves = 3.5;
        const thickness = radius * 0.12;

        const t = noise.noise2DNormalized(i, 0);
        const spineX = -length / 2 + t * length;
        const spineY = Math.sin(t * Math.PI * 2 * waves) * amplitude;

        const dx = length;
        const dy = Math.cos(t * Math.PI * 2 * waves) * amplitude * Math.PI * 2 * waves;
        const mag = Math.sqrt(dx * dx + dy * dy);
        const nx = -dy / mag;
        const ny = dx / mag;

        const perpOffset = (noise.noise2DNormalized(i, 1) - 0.5) * thickness * 1.8;
        x = spineX + nx * perpOffset;
        y = spineY + ny * perpOffset;
      } else {
        // Spherical distribution
        const angle = (i / dotCount) * Math.PI * 2 + noise.noise2D(i, 0) * 0.5;
        const dist = radius * 0.3 + noise.noise2D(i, 1) * radius * 0.5;
        x = Math.cos(angle) * dist;
        y = Math.sin(angle) * dist;
      }

      const dotSize = 1 + noise.noise2DNormalized(i, 2) * 2;
      graphics.fillCircle(x, y, dotSize);
    }
  }

  /**
   * Render organelles based on config
   */
  private renderOrganelles(
    config: ProceduralCellConfig,
    noise: SimplexNoise,
    random: SeededRandom
  ): Phaser.GameObjects.Graphics[] {
    // Skip organelles for bacteria with specific shapes - they appear as solid shapes
    if (config.shape === 'spiral' || config.shape === 'rod') {
      return [];
    }

    const organelles: Phaser.GameObjects.Graphics[] = [];
    const { baseRadius } = config.membrane;

    for (const organelleConfig of config.organelles) {
      const rendered = this.organelleRenderer.renderOrganelle(
        organelleConfig.type,
        baseRadius,
        organelleConfig.density ?? 1,
        noise,
        random
      );
      organelles.push(...rendered);
    }

    return organelles;
  }

  /**
   * Render nucleus for eukaryotic cells
   */
  private renderNucleus(
    config: ProceduralCellConfig,
    noise: SimplexNoise
  ): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    const nucleusConfig = config.nucleus!;
    const { baseRadius } = config.membrane;

    const nucleusRadius = baseRadius * nucleusConfig.relativeSize;

    // Position (center or offset)
    let offsetX = 0;
    let offsetY = 0;
    if (nucleusConfig.position === 'offset') {
      offsetX = noise.noise2D(0, 0) * baseRadius * 0.2;
      offsetY = noise.noise2D(1, 1) * baseRadius * 0.2;
    }

    // Nuclear envelope (double membrane look)
    const outerRadius = nucleusRadius;
    const innerRadius = nucleusRadius * 0.9;

    // Outer membrane
    graphics.fillStyle(CELL_COLORS.nucleus, 0.6);
    graphics.fillCircle(offsetX, offsetY, outerRadius);

    // Inner membrane (slightly lighter)
    graphics.fillStyle(CELL_COLORS.nucleus, 0.4);
    graphics.fillCircle(offsetX, offsetY, innerRadius);

    // Chromatin (DNA material inside)
    this.renderChromatin(graphics, offsetX, offsetY, innerRadius * 0.8, nucleusConfig.chromatinDensity, noise);

    // Nucleolus (dense spot inside nucleus)
    for (let i = 0; i < nucleusConfig.nucleolusCount; i++) {
      const nAngle = noise.noise2D(i * 2, 0) * Math.PI * 2;
      const nDist = noise.noise2DNormalized(i * 2, 1) * innerRadius * 0.4;
      const nx = offsetX + Math.cos(nAngle) * nDist;
      const ny = offsetY + Math.sin(nAngle) * nDist;
      const nRadius = innerRadius * 0.15 + noise.noise2D(i, 2) * innerRadius * 0.05;

      graphics.fillStyle(CELL_COLORS.nucleolus, 0.8);
      graphics.fillCircle(nx, ny, nRadius);
    }

    // Nuclear envelope border
    graphics.lineStyle(2, CELL_COLORS.nucleus, 0.8);
    graphics.strokeCircle(offsetX, offsetY, outerRadius);

    return graphics;
  }

  /**
   * Render chromatin strands inside nucleus
   */
  private renderChromatin(
    graphics: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    radius: number,
    density: number,
    noise: SimplexNoise
  ): void {
    const strandCount = Math.floor(density * 15);
    graphics.lineStyle(1, CELL_COLORS.nucleolus, 0.3);

    for (let i = 0; i < strandCount; i++) {
      const startAngle = noise.noise2D(i, 10) * Math.PI * 2;
      const startDist = noise.noise2DNormalized(i, 11) * radius * 0.8;
      const startX = cx + Math.cos(startAngle) * startDist;
      const startY = cy + Math.sin(startAngle) * startDist;

      graphics.beginPath();
      graphics.moveTo(startX, startY);

      // Draw wavy line
      const segments = 5;
      for (let j = 1; j <= segments; j++) {
        const t = j / segments;
        const angle = startAngle + noise.noise2D(i, j) * 0.5;
        const dist = startDist + noise.noise2D(i + j, 0) * radius * 0.3;
        const x = cx + Math.cos(angle + t * 0.5) * dist;
        const y = cy + Math.sin(angle + t * 0.5) * dist;
        graphics.lineTo(x, y);
      }

      graphics.strokePath();
    }
  }

  /**
   * Render cell membrane (outer boundary)
   */
  private renderMembrane(
    config: ProceduralCellConfig,
    noise: SimplexNoise
  ): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    const { baseRadius, irregularity, thickness, color } = config.membrane;
    const shape = config.shape || 'spherical';
    const membraneColor = hslToHex(color);

    if (shape === 'spiral') {
      // Custom spiral rendering - no Catmull-Rom smoothing
      const spinePoints = this.generateSpiralShape(baseRadius, irregularity, 48, noise);
      const params = this.getSpiralParams(baseRadius);

      // Draw with glow effect layers (outer glow first)
      this.drawSpiralShape(graphics, spinePoints, params.thickness + 2, 0, 0, membraneColor, 0.1, thickness + 4);
      this.drawSpiralShape(graphics, spinePoints, params.thickness + 1, 0, 0, membraneColor, 0.2, thickness + 2);
      this.drawSpiralShape(graphics, spinePoints, params.thickness, 0, 0, membraneColor, 0.8, thickness);

      // Inner highlight
      const innerSpine = this.generateSpiralShape(baseRadius * 0.98, irregularity, 48, noise);
      this.drawSpiralShape(graphics, innerSpine, params.thickness * 0.95, 0, 0, 0xFFFFFF, 0.2, thickness * 0.5);

      // Draw flagella (moustaches) - use fresh lineStyle after spiral drawing
      this.drawSpiralFlagella(graphics, spinePoints, params.thickness, membraneColor);
    } else if (shape === 'rod') {
      // Custom rod rendering - no Catmull-Rom smoothing
      // Draw with glow effect layers
      this.drawRodShape(graphics, baseRadius + 2, 0, 0, membraneColor, 0.1, thickness + 4);
      this.drawRodShape(graphics, baseRadius + 1, 0, 0, membraneColor, 0.2, thickness + 2);
      this.drawRodShape(graphics, baseRadius, 0, 0, membraneColor, 0.8, thickness);

      // Inner highlight
      this.drawRodShape(graphics, baseRadius * 0.98, 0, 0, 0xFFFFFF, 0.2, thickness * 0.5);
    } else {
      // Standard shape rendering with Catmull-Rom smoothing
      const points = this.generateShapePoints(config, baseRadius, irregularity, 48, noise);

      // Outer glow
      graphics.lineStyle(thickness + 4, membraneColor, 0.1);
      this.drawSmoothCurve(graphics, points);

      // Middle glow
      graphics.lineStyle(thickness + 2, membraneColor, 0.2);
      this.drawSmoothCurve(graphics, points);

      // Main membrane line
      graphics.lineStyle(thickness, membraneColor, 0.8);
      this.drawSmoothCurve(graphics, points);

      // Inner highlight
      graphics.lineStyle(thickness * 0.5, 0xFFFFFF, 0.2);
      const innerPoints = this.generateShapePoints(config, baseRadius * 0.98, irregularity, 48, noise);
      this.drawSmoothCurve(graphics, innerPoints);
    }

    return graphics;
  }

  /**
   * Render glow and highlight effects
   */
  private renderEffects(config: ProceduralCellConfig): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    const { baseRadius } = config.membrane;
    const shape = config.shape || 'spherical';

    // Subtle outer glow - adapt to cell shape
    const glowColor = hslToHex(config.membrane.color);

    if (shape === 'rod') {
      // Elongated ellipse for rod/capsule shape
      const halfLength = baseRadius * 1.5;
      const halfHeight = baseRadius * 0.18;
      graphics.fillStyle(glowColor, 0.05);
      graphics.fillEllipse(0, 0, halfLength * 2.3, halfHeight * 4.0);
    } else if (shape === 'spiral') {
      // No additional glow for spiral - the filled ribbon shape is enough
      // Skip to avoid circular appearance
    } else {
      graphics.fillStyle(glowColor, 0.05);
      graphics.fillCircle(0, 0, baseRadius * 1.1);
    }

    return graphics;
  }

  /**
   * Generate shape points based on cell shape type
   */
  generateShapePoints(
    config: ProceduralCellConfig,
    radius: number,
    irregularity: number,
    pointCount: number,
    noise: SimplexNoise
  ): Vector2[] {
    const shape = config.shape || 'spherical';

    switch (shape) {
      case 'rod':
        return this.generateRodShape(radius, irregularity, pointCount, noise);
      case 'spiral':
        return this.generateSpiralShape(radius, irregularity, pointCount, noise);
      default:
        return this.generateOrganicShape(radius, irregularity, pointCount, noise);
    }
  }

  /**
   * Generate organic shape points using Simplex noise (for spherical/coccus)
   */
  generateOrganicShape(
    radius: number,
    irregularity: number,
    pointCount: number,
    noise: SimplexNoise
  ): Vector2[] {
    const points: Vector2[] = [];

    for (let i = 0; i < pointCount; i++) {
      const angle = (i / pointCount) * Math.PI * 2;

      // Use multiple noise octaves for more organic feel
      const n1 = noise.noise2D(Math.cos(angle) * 2, Math.sin(angle) * 2);
      const n2 = noise.noise2D(Math.cos(angle) * 4, Math.sin(angle) * 4) * 0.5;
      const noiseVal = n1 + n2;

      const r = radius * (1 + noiseVal * irregularity * 0.3);

      points.push({
        x: Math.cos(angle) * r,
        y: Math.sin(angle) * r,
      });
    }

    return points;
  }

  /**
   * Generate rod/bacillus shape (elongated capsule)
   * Based on reference: elongated capsule with rounded ends
   * Creates a pill/capsule shape that is clearly wider than tall
   */
  private generateRodShape(
    radius: number,
    irregularity: number,
    pointCount: number,
    noise: SimplexNoise
  ): Vector2[] {
    const points: Vector2[] = [];

    // Rod dimensions - VERY elongated for clear visual distinction (~6:1 aspect ratio)
    const halfHeight = radius * 0.22; // Thinner rod
    const halfLength = radius * 1.4; // Much longer rod

    // Generate points around an elongated capsule shape
    for (let i = 0; i < pointCount; i++) {
      const t = i / pointCount;
      let x: number, y: number;

      if (t < 0.25) {
        // Left cap (semi-circle)
        const angle = Math.PI * 0.5 + (t / 0.25) * Math.PI;
        x = -halfLength + halfHeight + Math.cos(angle) * halfHeight;
        y = Math.sin(angle) * halfHeight;
      } else if (t < 0.5) {
        // Top edge (straight line)
        const progress = (t - 0.25) / 0.25;
        x = -halfLength + halfHeight + progress * (halfLength * 2 - halfHeight * 2);
        y = halfHeight;
      } else if (t < 0.75) {
        // Right cap (semi-circle)
        const angle = -Math.PI * 0.5 + ((t - 0.5) / 0.25) * Math.PI;
        x = halfLength - halfHeight + Math.cos(angle) * halfHeight;
        y = Math.sin(angle) * halfHeight;
      } else {
        // Bottom edge (straight line)
        const progress = (t - 0.75) / 0.25;
        x = halfLength - halfHeight - progress * (halfLength * 2 - halfHeight * 2);
        y = -halfHeight;
      }

      // Add subtle noise for organic feel
      const noiseVal = noise.noise2D(x * 0.05, y * 0.1) * irregularity * halfHeight * 0.15;
      x += noiseVal;
      y += noise.noise2D(x * 0.05 + 100, y * 0.1) * irregularity * halfHeight * 0.1;

      points.push({ x, y });
    }

    return points;
  }

  /**
   * Generate spiral/spirillum shape (corkscrew/helical bacteria)
   * Returns spine points and thickness info for special rendering
   */
  private generateSpiralShape(
    radius: number,
    _irregularity: number,
    pointCount: number,
    _noise: SimplexNoise
  ): Vector2[] {
    // For spiral shapes, we return spine points directly
    // The rendering will be handled by drawSpiralShape instead of drawSmoothCurve
    const points: Vector2[] = [];
    const length = radius * 5;
    const amplitude = radius * 0.5;
    const waves = 3.5;

    for (let i = 0; i <= pointCount; i++) {
      const t = i / pointCount;
      const x = -length / 2 + t * length;
      const y = Math.sin(t * Math.PI * 2 * waves) * amplitude;
      points.push({ x, y });
    }

    return points;
  }

  /**
   * Get spiral shape parameters for consistent rendering
   */
  private getSpiralParams(radius: number): { length: number; amplitude: number; waves: number; thickness: number } {
    return {
      length: radius * 5,
      amplitude: radius * 0.5,
      waves: 3.5,
      thickness: radius * 0.18,
    };
  }

  /**
   * Draw rod/bacillus shape with detailed layers like a real bacteria
   * Includes: capsule, cell wall, membrane, cytoplasm, nucleoid, pili
   */
  private drawRodShape(
    graphics: Phaser.GameObjects.Graphics,
    radius: number,
    fillColor: number,
    fillAlpha: number,
    strokeColor: number,
    strokeAlpha: number,
    strokeWidth: number
  ): void {
    // Dimensions for the elongated capsule
    const height = radius * 0.5;
    const width = radius * 2.8;

    // Only draw details when filling (cytoplasm layer)
    if (fillAlpha > 0) {
      this.drawDetailedBacillus(graphics, width, height, fillColor, fillAlpha);
    }

    // Draw membrane stroke
    if (strokeWidth > 0 && strokeAlpha > 0) {
      graphics.lineStyle(strokeWidth, strokeColor, strokeAlpha);
      graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, height / 2);
    }
  }

  /**
   * Draw detailed bacillus with multiple layers
   */
  private drawDetailedBacillus(
    graphics: Phaser.GameObjects.Graphics,
    width: number,
    height: number,
    _baseColor: number,
    alpha: number
  ): void {
    const cornerRadius = height / 2;

    // Layer 1: Capsule (outermost - cream/pink color)
    const capsuleExtra = height * 0.25;
    graphics.fillStyle(0xF5DEB3, alpha * 0.4); // Wheat/cream color
    graphics.fillRoundedRect(
      -width / 2 - capsuleExtra / 2,
      -height / 2 - capsuleExtra / 2,
      width + capsuleExtra,
      height + capsuleExtra,
      cornerRadius + capsuleExtra / 2
    );

    // Draw pili (hair-like projections) from capsule
    this.drawPili(graphics, width, height, capsuleExtra);

    // Layer 2: Cell wall (pink layer)
    const wallInset = height * 0.08;
    graphics.fillStyle(0xDDA0DD, alpha * 0.6); // Plum/pink color
    graphics.fillRoundedRect(
      -width / 2 + wallInset,
      -height / 2 + wallInset,
      width - wallInset * 2,
      height - wallInset * 2,
      cornerRadius - wallInset
    );

    // Layer 3: Cell membrane (dark blue)
    const membraneInset = height * 0.15;
    graphics.fillStyle(0x1E3A5F, alpha * 0.9); // Dark blue
    graphics.fillRoundedRect(
      -width / 2 + membraneInset,
      -height / 2 + membraneInset,
      width - membraneInset * 2,
      height - membraneInset * 2,
      cornerRadius - membraneInset
    );

    // Layer 4: Cytoplasm (teal interior)
    const cytoInset = height * 0.22;
    graphics.fillStyle(0x20B2AA, alpha * 0.8); // Light sea green/teal
    graphics.fillRoundedRect(
      -width / 2 + cytoInset,
      -height / 2 + cytoInset,
      width - cytoInset * 2,
      height - cytoInset * 2,
      cornerRadius - cytoInset
    );

    // Layer 5: Nucleoid regions (lighter cloudy areas - DNA)
    this.drawNucleoid(graphics, width, height, cytoInset, alpha);

    // Add cytoplasm texture
    this.drawCytoplasmDetails(graphics, width, height, cytoInset, alpha);
  }

  /**
   * Draw pili (hair-like projections) around the bacteria
   */
  private drawPili(
    graphics: Phaser.GameObjects.Graphics,
    width: number,
    height: number,
    capsuleExtra: number
  ): void {
    graphics.lineStyle(1, 0xC71585, 0.6); // Medium violet red

    const piliCount = 40;
    const halfWidth = width / 2 + capsuleExtra / 2;
    const halfHeight = height / 2 + capsuleExtra / 2;

    for (let i = 0; i < piliCount; i++) {
      const t = i / piliCount;
      let startX: number, startY: number;
      let angle: number;

      if (t < 0.25) {
        // Top edge
        const progress = t / 0.25;
        startX = -halfWidth * 0.7 + progress * halfWidth * 1.4;
        startY = -halfHeight;
        angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.8;
      } else if (t < 0.5) {
        // Right cap
        const progress = (t - 0.25) / 0.25;
        const capAngle = -Math.PI / 2 + progress * Math.PI;
        startX = halfWidth * 0.7 + Math.cos(capAngle) * halfHeight * 0.3;
        startY = Math.sin(capAngle) * halfHeight;
        angle = capAngle + (Math.random() - 0.5) * 0.5;
      } else if (t < 0.75) {
        // Bottom edge
        const progress = (t - 0.5) / 0.25;
        startX = halfWidth * 0.7 - progress * halfWidth * 1.4;
        startY = halfHeight;
        angle = Math.PI / 2 + (Math.random() - 0.5) * 0.8;
      } else {
        // Left cap
        const progress = (t - 0.75) / 0.25;
        const capAngle = Math.PI / 2 + progress * Math.PI;
        startX = -halfWidth * 0.7 + Math.cos(capAngle) * halfHeight * 0.3;
        startY = Math.sin(capAngle) * halfHeight;
        angle = capAngle + (Math.random() - 0.5) * 0.5;
      }

      // Draw wavy pilus
      const piliLength = height * 0.4 + Math.random() * height * 0.3;
      graphics.beginPath();
      graphics.moveTo(startX, startY);

      const segments = 5;
      for (let j = 1; j <= segments; j++) {
        const dist = (j / segments) * piliLength;
        const wave = Math.sin(j * 2) * 2;
        const endX = startX + Math.cos(angle) * dist + Math.cos(angle + Math.PI / 2) * wave;
        const endY = startY + Math.sin(angle) * dist + Math.sin(angle + Math.PI / 2) * wave;
        graphics.lineTo(endX, endY);
      }
      graphics.strokePath();
    }
  }

  /**
   * Draw flagella (whip-like projections) on spiral bacteria
   * These extend from the ends and along the sides of the spiral body
   */
  private drawSpiralFlagella(
    graphics: Phaser.GameObjects.Graphics,
    spinePoints: Vector2[],
    thickness: number,
    color: number,
    time: number = 0
  ): void {
    // Get key points along the spiral
    const startPoint = spinePoints[0];
    const endPoint = spinePoints[spinePoints.length - 1];

    // Flagella length based on body thickness - make them longer
    const flagellaLength = thickness * 6;

    // Use thick visible lines - force a minimum width
    graphics.lineStyle(2, color, 1.0);

    // Left end flagella - extend outward from start point (with phase offsets for variety)
    this.drawWavyFlagellum(graphics, startPoint.x, startPoint.y, -1, -0.4, flagellaLength, time, 0);
    this.drawWavyFlagellum(graphics, startPoint.x, startPoint.y, -1, 0.5, flagellaLength * 0.9, time, Math.PI * 0.5);

    // Right end flagella - extend outward from end point
    this.drawWavyFlagellum(graphics, endPoint.x, endPoint.y, 1, -0.3, flagellaLength, time, Math.PI * 0.3);
    this.drawWavyFlagellum(graphics, endPoint.x, endPoint.y, 1, 0.4, flagellaLength * 0.85, time, Math.PI * 0.8);

    // Side flagella at wave peaks/troughs
    graphics.lineStyle(1.5, color, 0.8);
    const peakIndices = [
      Math.floor(spinePoints.length * 0.2),
      Math.floor(spinePoints.length * 0.4),
      Math.floor(spinePoints.length * 0.6),
      Math.floor(spinePoints.length * 0.8),
    ];

    for (let i = 0; i < peakIndices.length; i++) {
      const idx = peakIndices[i];
      const point = spinePoints[idx];
      const yDir = point.y > 0 ? 1 : -1;
      this.drawWavyFlagellum(graphics, point.x, point.y, 0.15, yDir, flagellaLength * 0.4, time, i * Math.PI * 0.4);
    }
  }

  /**
   * Draw a wavy flagellum using a single continuous path with animation
   */
  private drawWavyFlagellum(
    graphics: Phaser.GameObjects.Graphics,
    startX: number,
    startY: number,
    dirX: number,
    dirY: number,
    length: number,
    time: number = 0,
    phaseOffset: number = 0
  ): void {
    const segments = 12;
    const waveAmp = length * 0.18;

    // Normalize direction
    const mag = Math.sqrt(dirX * dirX + dirY * dirY);
    const nx = dirX / mag;
    const ny = dirY / mag;

    // Perpendicular direction for wave
    const px = -ny;
    const py = nx;

    // Animation phase - creates wave motion along flagellum
    const animPhase = time * 0.004 + phaseOffset;

    // Build single continuous path
    graphics.beginPath();
    graphics.moveTo(startX, startY);

    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const baseX = startX + nx * length * t;
      const baseY = startY + ny * length * t;

      // Wave with animation - wave propagates outward and tapers off
      const wave = Math.sin(t * Math.PI * 2.5 + animPhase) * waveAmp * (1 - t * 0.4);
      const x = baseX + px * wave;
      const y = baseY + py * wave;

      graphics.lineTo(x, y);
    }

    graphics.strokePath();
  }

  /**
   * Draw nucleoid regions (DNA material)
   */
  private drawNucleoid(
    graphics: Phaser.GameObjects.Graphics,
    width: number,
    height: number,
    cytoInset: number,
    alpha: number
  ): void {
    const innerWidth = width - cytoInset * 2;
    const innerHeight = height - cytoInset * 2;

    // Draw cloudy nucleoid regions
    graphics.fillStyle(0x87CEEB, alpha * 0.5); // Sky blue - lighter than cytoplasm

    // Multiple irregular blob regions
    const blobPositions = [
      { x: -innerWidth * 0.25, y: 0, w: innerWidth * 0.3, h: innerHeight * 0.6 },
      { x: innerWidth * 0.1, y: -innerHeight * 0.1, w: innerWidth * 0.25, h: innerHeight * 0.5 },
      { x: innerWidth * 0.0, y: innerHeight * 0.1, w: innerWidth * 0.2, h: innerHeight * 0.4 },
    ];

    for (const blob of blobPositions) {
      // Draw irregular blob shape
      graphics.beginPath();
      const points = 12;
      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const rx = blob.w / 2 * (0.7 + Math.sin(angle * 3) * 0.3);
        const ry = blob.h / 2 * (0.7 + Math.cos(angle * 2) * 0.3);
        const px = blob.x + Math.cos(angle) * rx;
        const py = blob.y + Math.sin(angle) * ry;
        if (i === 0) {
          graphics.moveTo(px, py);
        } else {
          graphics.lineTo(px, py);
        }
      }
      graphics.closePath();
      graphics.fillPath();
    }
  }

  /**
   * Draw cytoplasm texture details
   */
  private drawCytoplasmDetails(
    graphics: Phaser.GameObjects.Graphics,
    width: number,
    height: number,
    cytoInset: number,
    alpha: number
  ): void {
    const innerWidth = width - cytoInset * 2;
    const innerHeight = height - cytoInset * 2;

    // Small dots representing ribosomes
    graphics.fillStyle(0x40E0D0, alpha * 0.6); // Turquoise

    for (let i = 0; i < 30; i++) {
      const x = (Math.random() - 0.5) * innerWidth * 0.8;
      const y = (Math.random() - 0.5) * innerHeight * 0.7;
      const size = 1 + Math.random() * 1.5;
      graphics.fillCircle(x, y, size);
    }
  }

  /**
   * Draw spiral/spirillum shape as a filled wavy ribbon
   */
  private drawSpiralShape(
    graphics: Phaser.GameObjects.Graphics,
    spinePoints: Vector2[],
    thickness: number,
    fillColor: number,
    fillAlpha: number,
    strokeColor: number,
    strokeAlpha: number,
    strokeWidth: number
  ): void {
    if (spinePoints.length < 2) return;

    // Calculate normals for each spine point
    const topEdge: Vector2[] = [];
    const bottomEdge: Vector2[] = [];

    for (let i = 0; i < spinePoints.length; i++) {
      const curr = spinePoints[i];
      const prev = spinePoints[Math.max(0, i - 1)];
      const next = spinePoints[Math.min(spinePoints.length - 1, i + 1)];

      // Tangent direction
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      const mag = Math.sqrt(dx * dx + dy * dy);

      // Normal (perpendicular)
      const nx = -dy / mag;
      const ny = dx / mag;

      topEdge.push({ x: curr.x + nx * thickness, y: curr.y + ny * thickness });
      bottomEdge.push({ x: curr.x - nx * thickness, y: curr.y - ny * thickness });
    }

    // Draw filled shape
    graphics.fillStyle(fillColor, fillAlpha);
    graphics.beginPath();

    // Top edge (left to right)
    graphics.moveTo(topEdge[0].x, topEdge[0].y);
    for (let i = 1; i < topEdge.length; i++) {
      graphics.lineTo(topEdge[i].x, topEdge[i].y);
    }

    // Right cap (semicircle)
    const rightIdx = spinePoints.length - 1;
    const rightCenter = spinePoints[rightIdx];
    const rightAngleStart = Math.atan2(topEdge[rightIdx].y - rightCenter.y, topEdge[rightIdx].x - rightCenter.x);
    for (let i = 1; i <= 6; i++) {
      const angle = rightAngleStart - (i / 6) * Math.PI;
      graphics.lineTo(rightCenter.x + Math.cos(angle) * thickness, rightCenter.y + Math.sin(angle) * thickness);
    }

    // Bottom edge (right to left)
    for (let i = bottomEdge.length - 1; i >= 0; i--) {
      graphics.lineTo(bottomEdge[i].x, bottomEdge[i].y);
    }

    // Left cap (semicircle)
    const leftCenter = spinePoints[0];
    const leftAngleStart = Math.atan2(bottomEdge[0].y - leftCenter.y, bottomEdge[0].x - leftCenter.x);
    for (let i = 1; i <= 6; i++) {
      const angle = leftAngleStart - (i / 6) * Math.PI;
      graphics.lineTo(leftCenter.x + Math.cos(angle) * thickness, leftCenter.y + Math.sin(angle) * thickness);
    }

    graphics.closePath();
    graphics.fillPath();

    // Draw stroke (membrane outline)
    if (strokeWidth > 0 && strokeAlpha > 0) {
      graphics.lineStyle(strokeWidth, strokeColor, strokeAlpha);
      graphics.beginPath();

      // Top edge
      graphics.moveTo(topEdge[0].x, topEdge[0].y);
      for (let i = 1; i < topEdge.length; i++) {
        graphics.lineTo(topEdge[i].x, topEdge[i].y);
      }

      // Right cap
      const rightAngleStart2 = Math.atan2(topEdge[rightIdx].y - rightCenter.y, topEdge[rightIdx].x - rightCenter.x);
      for (let i = 1; i <= 6; i++) {
        const angle = rightAngleStart2 - (i / 6) * Math.PI;
        graphics.lineTo(rightCenter.x + Math.cos(angle) * thickness, rightCenter.y + Math.sin(angle) * thickness);
      }

      // Bottom edge
      for (let i = bottomEdge.length - 1; i >= 0; i--) {
        graphics.lineTo(bottomEdge[i].x, bottomEdge[i].y);
      }

      // Left cap
      const leftAngleStart2 = Math.atan2(bottomEdge[0].y - leftCenter.y, bottomEdge[0].x - leftCenter.x);
      for (let i = 1; i <= 6; i++) {
        const angle = leftAngleStart2 - (i / 6) * Math.PI;
        graphics.lineTo(leftCenter.x + Math.cos(angle) * thickness, leftCenter.y + Math.sin(angle) * thickness);
      }

      graphics.closePath();
      graphics.strokePath();
    }
  }

  /**
   * Draw a smooth curve through points using line segments
   * Approximates Catmull-Rom spline for smoothness
   */
  private drawSmoothCurve(
    graphics: Phaser.GameObjects.Graphics,
    points: Vector2[]
  ): void {
    if (points.length < 3) return;

    graphics.beginPath();

    // Start at first point
    graphics.moveTo(points[0].x, points[0].y);

    // Draw smooth curve through points using Catmull-Rom interpolation
    const segmentsPerCurve = 8;

    for (let i = 0; i < points.length; i++) {
      const p0 = points[(i - 1 + points.length) % points.length];
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p3 = points[(i + 2) % points.length];

      // Subdivide each curve segment for smoothness
      for (let j = 1; j <= segmentsPerCurve; j++) {
        const t = j / segmentsPerCurve;
        const t2 = t * t;
        const t3 = t2 * t;

        // Catmull-Rom spline formula
        const x = 0.5 * (
          (2 * p1.x) +
          (-p0.x + p2.x) * t +
          (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
          (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3
        );
        const y = 0.5 * (
          (2 * p1.y) +
          (-p0.y + p2.y) * t +
          (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
          (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3
        );

        graphics.lineTo(x, y);
      }
    }

    graphics.closePath();
    graphics.strokePath();
  }

  /**
   * Update cell membrane for animation (morphing)
   */
  updateMembrane(
    graphics: Phaser.GameObjects.Graphics,
    config: ProceduralCellConfig,
    noise: SimplexNoise,
    time: number
  ): void {
    const { baseRadius, irregularity, thickness, color } = config.membrane;
    const shape = config.shape || 'spherical';
    const membraneColor = hslToHex(color);

    graphics.clear();

    if (shape === 'rod') {
      // Rod shape - redraw with slight pulse effect
      const pulseScale = 1 + Math.sin(time * 0.003) * 0.02;
      this.drawRodShape(graphics, baseRadius * pulseScale, 0, 0, membraneColor, 0.2, thickness + 2);
      this.drawRodShape(graphics, baseRadius * pulseScale, 0, 0, membraneColor, 0.8, thickness);
    } else if (shape === 'spiral') {
      // Spiral shape - redraw with slight undulation
      const spinePoints = this.generateSpiralShape(baseRadius, irregularity, 48, noise);
      const params = this.getSpiralParams(baseRadius);
      const pulseThickness = params.thickness + Math.sin(time * 0.002) * 0.5;
      this.drawSpiralShape(graphics, spinePoints, pulseThickness, 0, 0, membraneColor, 0.2, thickness + 2);
      this.drawSpiralShape(graphics, spinePoints, pulseThickness, 0, 0, membraneColor, 0.8, thickness);

      // Draw flagella (moustaches) with animation
      this.drawSpiralFlagella(graphics, spinePoints, params.thickness, membraneColor, time);
    } else {
      // Spherical shape - use morphing animation
      const points = this.generateMorphingShape(baseRadius, irregularity, 48, noise, time);

      // Redraw membrane
      graphics.lineStyle(thickness + 2, membraneColor, 0.2);
      this.drawSmoothCurve(graphics, points);

      graphics.lineStyle(thickness, membraneColor, 0.8);
      this.drawSmoothCurve(graphics, points);
    }
  }

  /**
   * Generate morphing shape using 3D noise (for animation)
   */
  private generateMorphingShape(
    radius: number,
    irregularity: number,
    pointCount: number,
    noise: SimplexNoise,
    time: number
  ): Vector2[] {
    const points: Vector2[] = [];

    for (let i = 0; i < pointCount; i++) {
      const angle = (i / pointCount) * Math.PI * 2;

      // Use 3D noise with time as Z for animation
      const n1 = noise.noise3D(Math.cos(angle) * 2, Math.sin(angle) * 2, time * 0.001);
      const n2 = noise.noise3D(Math.cos(angle) * 4, Math.sin(angle) * 4, time * 0.002) * 0.5;
      const noiseVal = n1 + n2;

      const r = radius * (1 + noiseVal * irregularity * 0.3);

      points.push({
        x: Math.cos(angle) * r,
        y: Math.sin(angle) * r,
      });
    }

    return points;
  }
}
